import { useCallback, useMemo } from 'react';
import {
	GROUP_BY_ASSIGNEE,
	GROUP_BY_CATEGORY,
	GROUP_BY_PRIORITY,
	GROUP_BY_STATUS,
} from '@atlassian/jira-business-constants';
import {
	useViewPreference,
	COLLAPSED_COLUMNS_PREFERENCE,
} from '@atlassian/jira-business-preferences';
import type { CollapsedColumns } from '../../common/types';
import { useGroupBy } from '../group-by';

const emptyColumnKeys: CollapsedColumns = {
	[GROUP_BY_STATUS]: [],
	[GROUP_BY_ASSIGNEE]: [],
	[GROUP_BY_PRIORITY]: [],
	[GROUP_BY_CATEGORY]: [],
};

/**
 * To be deleted when jwm.board-awesome-enhancements is cleaned up
 * @deprecated Use useExpandCollapse
 */
export const useCollapsedColumns = () => {
	const [groupBy] = useGroupBy();
	const [collapsedColumnsByGroupBy, updateCollapsedColumns] = useViewPreference(
		COLLAPSED_COLUMNS_PREFERENCE,
	);

	const collapsedColumns: string[] = useMemo(() => {
		if (collapsedColumnsByGroupBy && typeof collapsedColumnsByGroupBy === 'object') {
			const collapsedKeys = collapsedColumnsByGroupBy[groupBy];
			if (Array.isArray(collapsedKeys)) {
				return collapsedKeys;
			}
		}
		return [];
	}, [collapsedColumnsByGroupBy, groupBy]);

	const setCollapsedColumns = useCallback(
		(newColumnKeys: string[], isCollapsed: boolean) => {
			let newCollapsedKeys = [];
			if (isCollapsed) {
				newCollapsedKeys = [...collapsedColumns, ...newColumnKeys];
			} else {
				// set expand columns
				newCollapsedKeys = collapsedColumns.filter(
					(collapsedColumnKey) => !newColumnKeys.includes(collapsedColumnKey),
				);
			}
			updateCollapsedColumns({
				...(collapsedColumnsByGroupBy ?? emptyColumnKeys),
				[groupBy]: newCollapsedKeys,
			});
		},
		[collapsedColumns, groupBy, collapsedColumnsByGroupBy, updateCollapsedColumns],
	);

	return [collapsedColumns, setCollapsedColumns] as const;
};
